/**
 * StarRating Component
 * Displays star rating (read-only or interactive)
 */

import { cn } from '@shared/lib/utils'
import { Star } from 'lucide-react'

// ============================================================================
// Types
// ============================================================================

interface StarRatingProps {
  rating: number // 0-5
  maxStars?: number // default 5
  size?: 'sm' | 'md' | 'lg'
  showValue?: boolean // Show numeric value next to stars
  className?: string
  onChange?: (rating: number) => void // For interactive rating
}

// ============================================================================
// Component
// ============================================================================

export function StarRating({
  rating,
  maxStars = 5,
  size = 'md',
  showValue = true,
  className,
  onChange,
}: StarRatingProps) {
  const isInteractive = !!onChange

  const sizeClasses = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5',
  }

  const textSizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  }

  const handleClick = (value: number) => {
    if (isInteractive) {
      onChange(value)
    }
  }

  return (
    <div className={cn('flex items-center gap-1', className)}>
      {/* Stars */}
      <div className="flex items-center">
        {Array.from({ length: maxStars }).map((_, index) => {
          const starValue = index + 1
          const isFilled = starValue <= Math.round(rating)
          const isPartial = starValue > rating && starValue - 1 < rating

          return (
            <button
              key={index}
              type="button"
              onClick={() => handleClick(starValue)}
              disabled={!isInteractive}
              className={cn(
                'transition-colors',
                isInteractive && 'cursor-pointer hover:scale-110',
                !isInteractive && 'cursor-default'
              )}
              aria-label={`${starValue} star${starValue > 1 ? 's' : ''}`}
            >
              <Star
                data-state={isFilled ? 'filled' : isPartial ? 'partial' : 'empty'}
                className={cn(
                  sizeClasses[size],
                  'transition-all',
                  isFilled && 'fill-ring text-ring',
                  !isFilled && !isPartial && 'fill-none text-muted-foreground',
                  isPartial && 'fill-ring text-ring'
                )}
              />
            </button>
          )
        })}
      </div>

      {/* Numeric Value */}
      {showValue && (
        <span className={cn('font-medium text-foreground', textSizeClasses[size])}>
          {rating.toFixed(1)}
        </span>
      )}
    </div>
  )
}
